import { CheckCircleOutlined } from "@ant-design/icons";
import { Button, Collapse, Grid } from "antd";
import { loQueEstaCargado } from "../../components/documentoCargado";
import { aportado } from "../../../../utils/documentoAportado";
import {
  esCartaDeConformacion,
  esDeclaracionDeResidencia,
  esDeclaracionUnificada,
  esRelacionDeParticipantes,
} from "../../../../utils/documentosGrupoConformado";

/**
 * El listado de documentos administrativos de una postulacion.
 *
 * La tarjeta de un documento ya puesto era una capa verde en posicion
 * absoluta encima de la normal. Cubria la de abajo mientras la tarjeta fuera
 * ancha; en la columna del wizard se veia el texto de las dos superpuesto.
 * Ahora el documento se dibuja en un estado o en el otro, en flujo normal,
 * asi que no hay nada que pueda taparse mal a ningun ancho.
 *
 * En pantalla angosta cada documento es un desplegable, uno por fila, porque
 * una rejilla de tarjetas con este contenido no cabe. Desde md se vuelven
 * tarjetas, de a dos por fila en pantalla ancha.
 *
 * Todo lo que la tarjeta necesita de la pantalla llega agrupado en
 * "acciones", porque son quince cosas y enumerarlas una por una como props
 * solo haria mas ruido. Que sean tantas dice lo enredada que esta la tarjeta
 * con los cuatro modales; separarla de ellos es trabajo aparte.
 */
export interface AccionesDocumentos {
  documentos: any[];
  documentosUsuario: any[];
  setDocumentosUsuario: (valor: any) => void;
  guardarArchivosAdministrativos: (...args: any[]) => any;
  eliminarArchivo: (...args: any[]) => any;
  DownloadFile: (...args: any[]) => any;
  /** Abre el documento cargado en el visor, sin tener que bajarlo. */
  verDocumento: (archivo: string) => void;
  formulariosEnLinea: any;
  setOpenResidenciaModal: (valor: boolean) => void;
  setOpenRelacionParticipantesModal: (valor: boolean) => void;
  setOpenRelacionCartaConformacionModal: (valor: boolean) => void;
  setOpenDeclaracionGCModal: (valor: boolean) => void;
  setParticipante: (valor: any) => void;
  setParticipantes: (valor: any) => void;
  setCelular: (valor: any) => void;
  setCorreo: (valor: any) => void;
}

interface IProps {
  acciones: AccionesDocumentos;
}

/** El documento esta puesto si tiene archivo, o si se firmo en linea. */
export const estaCargado = aportado;

export const ListaDocumentos = ({ acciones }: IProps) => {
  const {
    documentos,
    documentosUsuario,
    setDocumentosUsuario,
    guardarArchivosAdministrativos,
    eliminarArchivo,
    DownloadFile,
    verDocumento,
    formulariosEnLinea,
    setOpenResidenciaModal,
    setOpenRelacionParticipantesModal,
    setOpenRelacionCartaConformacionModal,
    setOpenDeclaracionGCModal,
    setParticipante,
    setParticipantes,
    setCelular,
    setCorreo,
  } = acciones;

  // En el primer render todavia no hay medida; se asume ancho, que es la
  // vista que no cambia de forma cuando la medida llega.
  const pantalla = Grid.useBreakpoint();
  const enMovil = pantalla.md === false;

  /** De a dos por fila desde md; mas abajo cada documento es un desplegable. */
  const claseColumna = "col-12 col-md-6 mt-3 mb-2 d-flex";

  /** Abre el modal que corresponda al formato que se llena en linea. */
  const abrirFormularioEnLinea = (data: any) => {
    if (data.participantes?.length > 0) {
      setParticipantes(data.participantes);
    }

    if (esDeclaracionDeResidencia(data) || esDeclaracionUnificada(data)) {
      if (data.participantes?.length > 0) {
        setParticipante((prev: any) => ({
          ...prev,
          nombreGrupo: data.participantes[0].nombreGrupo,
        }));
      }
      // Correo y telefono se restauran de lo que quedo guardado.
      if (data.correo) setCorreo(data.correo);
      if (data.telefono) setCelular(data.telefono);
      /*
        El formato unificado es siempre del grupo, lo diga o no el perfil
        guardado. El anterior se llama igual que la declaracion individual
        de persona natural, asi que ahi si hay que mirar con que perfil se
        presenta.
      */
      if (
        esDeclaracionUnificada(data) ||
        ["2", "3"].includes(localStorage.getItem("tipo_participante_postulacion") ?? "")
      ) {
        setOpenDeclaracionGCModal(true);
      } else {
        setOpenResidenciaModal(true);
      }
    }

    if (esRelacionDeParticipantes(data)) {
      setOpenRelacionParticipantesModal(true);
    }

    // Ya no se pide, pero lo diligenciado con el formato aparte se abre igual.
    if (esCartaDeConformacion(data)) {
      if (data.participantes?.length > 0) {
        setParticipante((prev: any) => ({
          ...prev,
          nombreGrupo: data.participantes[0].nombreGrupo,
        }));
      }
      setOpenRelacionCartaConformacionModal(true);
    }
  };

  const quitarDocumento = (e: any, data: any, index: number) => {
    const filterDoc = documentosUsuario.filter(
      (documentoAdmin: any) => documentoAdmin.id !== data.id
    );
    setDocumentosUsuario(filterDoc);
    eliminarArchivo(e, index, data.id);
  };

  /** El formato que la plataforma entrega para diligenciar, si lo hay. */
  const formato = (data: any) => {
    if (data.descripcion === "Datos personales") {
      return (
        <Button
          className="button-yellow-ghost p-0 text-start"
          style={{ height: "auto", whiteSpace: "normal" }}
          onClick={() => {
            window.open("https://forms.office.com/r/yi7M5MecNb", "_blank", "noopener,noreferrer");
          }}
        >
          https://forms.office.com/r/yi7M5MecNb
        </Button>
      );
    }

    if (data.url_documento) {
      return (
        <Button
          className="button-yellow-ghost p-0"
          onClick={() => {
            DownloadFile(data.url_documento);
          }}
        >
          DESCARGAR FORMATO
        </Button>
      );
    }

    return (
      <span className="font-size-12px" style={{ color: "#ff8900" }}>
        SIN FORMATO
      </span>
    );
  };

  /** Lo que se puede hacer con un documento que todavia no esta puesto. */
  const cuerpoPendiente = (data: any, index: number) => (
    <>
      <div className="mb-2">{formato(data)}</div>
      <div className="mt-auto">
        {formulariosEnLinea.includes(data.descripcion) ? (
          <Button className="button-azul-radius m-0" onClick={() => abrirFormularioEnLinea(data)}>
            Diligenciar en línea
          </Button>
        ) : (
          <Button
            className="button-gray-ghost p-0 m-0"
            onClick={() => {
              document.getElementById(`input-file-${data.id}`)?.click();
            }}
          >
            CARGAR DOCUMENTO
            <input
              id={`input-file-${data.id}`}
              className="inputs-ref"
              type="file"
              hidden
              onChange={async (e) => {
                guardarArchivosAdministrativos(data, e, index);
                e.target.value = "";
              }}
            />
          </Button>
        )}
      </div>
    </>
  );

  /**
   * Un documento ya puesto. Dice que quedo cargado: antes solo decia
   * "Cargado", asi que no habia forma de saber que archivo era sin borrarlo y
   * volver a subirlo.
   */
  const cuerpoCargado = (data: any, index: number) => (
    <>
      <div className="documento-archivo font-size-12px mb-2" title={loQueEstaCargado(data)}>
        {loQueEstaCargado(data)}
      </div>
      <div className="mt-auto d-flex flex-wrap align-items-center" style={{ gap: 24 }}>
        {/*
          Lo que se cargo se abre en el visor, que muestra el PDF sin bajarlo.
          Lo que se lleno en linea no es un archivo: se abre su formulario.
        */}
        <Button
          className="button-blanco-ghost text-uppercase font-family-Montserrat-Regular p-0"
          onClick={() =>
            data.url_participante
              ? verDocumento(data.url_participante)
              : abrirFormularioEnLinea(data)
          }
        >
          Ver
        </Button>
        <Button
          className="button-blanco-ghost text-uppercase font-family-Montserrat-Regular p-0"
          onClick={(e) => quitarDocumento(e, data, index)}
        >
          Eliminar
        </Button>
      </div>
    </>
  );

  const cuerpo = (data: any, index: number) =>
    estaCargado(data) ? cuerpoCargado(data, index) : cuerpoPendiente(data, index);

  const titulo = (data: any) => (
    <span className="font-family-Montserrat-Regular font-size-14px">
      {estaCargado(data) && <CheckCircleOutlined className="me-2" />}
      {data.descripcion}
    </span>
  );

  if (enMovil) {
    return (
      <Collapse className="mt-4 mb-5 documentos-desplegables">
        {documentos.map((data: any, index: number) => (
          <Collapse.Panel
            key={String(data.id ?? index)}
            className={estaCargado(data) ? "documento-cargado" : ""}
            header={titulo(data)}
          >
            <div className="d-flex flex-column">{cuerpo(data, index)}</div>
          </Collapse.Panel>
        ))}
      </Collapse>
    );
  }

  return (
    <div className="row mt-4 mb-5">
      {documentos.map((data: any, index: number) => (
        <div className={claseColumna} key={data.id ?? index}>
          <div
            className={`cards-documentos d-flex flex-column ${
              estaCargado(data) ? "cards-documentos-cargado" : ""
            }`}
          >
            <div className="mb-2">{titulo(data)}</div>
            {cuerpo(data, index)}
          </div>
        </div>
      ))}
    </div>
  );
};
